import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import UploadService from "../UploadService";
import { BlockBlobClient } from "@azure/storage-blob";
import { config } from "../../constants/config";
import { toast } from "react-toastify";
import { updateAsset } from "../../webapi/webapi";
import { checkFileIsAudio } from "../../components/utils/utils";
import { getUser, getIdToken } from "../../webapi/AuthService";
import moment from "moment";
import { UploadStatus } from "../UploadEnums";
import i18next from "i18next";

const assetLink = "/mam/asset/";

const initialState = {
  uploadQueue: [],
  azureStorageSasKey: "",
  maxBlockSizeInMB: 1,
  maxThreads: 4
};

export const processFile = createAsyncThunk(
  "upload/processFile",
  async (queueItem, { dispatch, getState }) => {
    try {
      const state = getState().upload;
      let token = await UploadService.getJwtToken(queueItem.fileName);
      if (!token) {
        dispatch(
          updateFileStatusAndProcessNextItem({
            id: queueItem.id,
            status: UploadStatus.ERROR
          })
        );
        return;
      } else {
        let azureStorageSasKey = await UploadService.getSasToken(
          token,
          queueItem.fileName
        );
        if (!azureStorageSasKey) {
          dispatch(
            updateFileStatusAndProcessNextItem({
              id: queueItem.id,
              status: UploadStatus.ERROR
            })
          );
          return;
        }

        const connectionString = `${config.AZURE_STORAGE_CONNECTION_STRING}${azureStorageSasKey}`;
        const blockClient = new BlockBlobClient(
          connectionString,
          config.AZURE_STORAGE_CONTAINER_NAME,
          queueItem.fileName
        );

        let threadCount = state.maxThreads;
        if (queueItem.blockQueue.length > 0) {
          const state = getState().upload;
          let queue = state.uploadQueue.slice();
          const index = queue.findIndex(x => x.id === queueItem.id);
          const currentItem = queue[index];
          const blockQueue = currentItem.blockQueue.filter(
            x => x.status === UploadStatus.PENDING
          );

          if (blockQueue.length < threadCount) {
            threadCount = blockQueue.length;
          }

          for (let index = 1; index <= threadCount; index++) {
            dispatch(
              uploadNextBlock({
                thread: index,
                total: threadCount,
                id: queueItem.id,
                blockClient
              })
            );
          }

          return;
        }

        let uncomittedBlocks = [];
        try {
          const uncommitted = await blockClient.getBlockList("uncommitted");
          if (uncommitted && Array.isArray(uncommitted.uncommittedBlocks)) {
            uncomittedBlocks = uncommitted.uncommittedBlocks.slice();
          }
        } catch (error) {}

        const file = queueItem.file;
        const blobName = queueItem.fileName;
        const maxBlockSize = state.maxBlockSizeInMB * 1024 * 1024;

        const totalBlocks = Math.ceil(file.size / maxBlockSize);

        let blockIds = [];
        let blockNumber = 0;
        while (blockNumber < totalBlocks) {
          const blockNrPadded = blockNumber.toString().padStart(6, "0");
          const blockId = btoa(`${blobName}|${blockNrPadded}`);
          blockIds.push(blockId);
          blockNumber++;
        }

        await dispatch(
          setFileBlockIds({
            id: queueItem.id,
            blockIds: blockIds,
            size: file.size
          })
        );

        const blockArray = blockIds.map((blockId, index) => {
          const blockIndex = uncomittedBlocks.findIndex(
            x => x.name === blockId
          );

          if (blockIndex >= 0) {
            dispatch(
              setCurrentFileProgress({
                id: queueItem.id,
                loaded: uncomittedBlocks[blockIndex].size
              })
            );
            return {
              blockId,
              block: null,
              status: UploadStatus.DONE,
              itemId: queueItem.id
            };
          } else {
            const blockOffset = index * maxBlockSize;
            const block = file.slice(
              blockOffset,
              Math.min(blockOffset + maxBlockSize, file.size)
            );

            return {
              blockId,
              block,
              status: UploadStatus.PENDING,
              itemId: queueItem.id
            };
          }
        });

        await dispatch(
          setFileBlockQueue({ id: queueItem.id, blockQueue: [...blockArray] })
        );

        //let threadCount = state.maxThreads;
        if (totalBlocks < threadCount) {
          threadCount = totalBlocks;
        }

        for (let index = 1; index <= threadCount; index++) {
          dispatch(
            uploadNextBlock({
              thread: index,
              total: threadCount,
              id: queueItem.id,
              blockClient
            })
          );
        }
        // });
      }
    } catch (error) {
      console.log(error);
    }
  }
);

const uploadNextBlock = createAsyncThunk(
  "upload/uploadNextBlock",
  async (data, { dispatch, getState }) => {
    const state = getState().upload;
    let queue = state.uploadQueue.slice();
    let index = queue.findIndex(x => x.id === data.id);
    const currentItem = queue[index];
    const blockQueue = currentItem.blockQueue;

    const pendingBlocks = [...blockQueue].filter(
      x => x.status === UploadStatus.PENDING
    );

    const completedBlocks = [...blockQueue].filter(
      x => x.status === UploadStatus.DONE
    );

    const isCompleted =
      blockQueue.length > 0 && completedBlocks.length === blockQueue.length;
    if (isCompleted) {
      try {
        await data.blockClient.commitBlockList(currentItem.blockIds);
        await dispatch(
          updateFileStatus({
            id: currentItem.id,
            status: UploadStatus.COMPLETED
          })
        );
        toast.success(
          `${i18next.t("upload_file")} ${currentItem.file.name} ${i18next.t(
            "upload_has_been_uploaded"
          )}`
        );

        const isAudio = checkFileIsAudio(currentItem.file.name);
        const user = getUser();
        const userId = parseInt(getIdToken(), 10);

        const createSource = () => ({
          UploadedDate: moment().format("YYYY-MM-DDTH:mm:ss"),
          UploadedByUser: user,
          UploadedByUserId: userId
        });

        const asset = {
          AssetID: currentItem.assetId,
          UploadedByUser: user,
          UploadedByUserId: userId,
          Audio: {
            Source: isAudio ? createSource() : null
          },
          Video: {
            Source: !isAudio ? createSource() : null
          }
        };
        await updateAsset(asset);
      } catch (error) {
        dispatch(
          updateFileStatus({
            id: currentItem.id,
            status: UploadStatus.ERROR
          })
        );
        toast.error(
          `${i18next.t("upload_error_uploading")} ${currentItem.file.name}`
        );
      } finally {
        dispatch(processQueue());
        return;
      }
    }

    switch (currentItem.status) {
      case UploadStatus.UPLOADING:
      case UploadStatus.CANCELLED:
      case UploadStatus.RESUMED:
      case UploadStatus.ERROR:
        break;
      default:
        dispatch(processQueue());
        return;
    }

    if (blockQueue.length === 0) {
      dispatch(processQueue());
      return;
    }
    if (pendingBlocks.length > 0) {
      const nextBlock = pendingBlocks[0];
      await dispatch(
        setFileBlockStatus({
          id: currentItem.id,
          blockId: nextBlock.blockId,
          status: UploadStatus.PROCESSING
        })
      );
      try {
        let uploaded = 0;
        await data.blockClient.stageBlock(
          nextBlock.blockId,
          nextBlock.block,
          nextBlock.block.size,
          {
            onProgress: ev => {
              dispatch(
                setCurrentFileProgress({
                  id: currentItem.id,
                  loaded: ev.loadedBytes - uploaded
                })
              );
              uploaded = ev.loadedBytes;
            }
          }
        );
        await dispatch(
          setFileBlockStatus({
            id: currentItem.id,
            blockId: nextBlock.blockId,
            status: UploadStatus.DONE
          })
        );
        dispatch(uploadNextBlock(data));
        return;
      } catch (error) {
        dispatch(
          updateFileStatus({
            id: currentItem.id,
            status: UploadStatus.ERROR
          })
        );
        return;
      }
    }
  }
);

export const uploadFile = createAsyncThunk(
  "upload/uploadToBlobStorageChunked",
  async (queueItem, { dispatch }) => {
    try {
      await dispatch(processFile(queueItem));
    } catch (error) {
      console.log(error);
    }
  }
);

export const processQueue = createAsyncThunk(
  "upload/processQueue",
  async (_, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState().upload;
      const isUploading =
        state.uploadQueue.filter(x =>
          [UploadStatus.UPLOADING, UploadStatus.RESUMED].includes(x.status)
        ).length > 0;

      if (isUploading) return;

      const pendingItems = state.uploadQueue.filter(x =>
        [UploadStatus.PENDING, UploadStatus.RESUMED].includes(x.status)
      );
      if (pendingItems.length > 0) {
        const nextQueueItem = pendingItems[0];

        await dispatch(
          updateFileStatus({
            id: nextQueueItem.id,
            status: UploadStatus.UPLOADING
          })
        );
        await dispatch(uploadFile(nextQueueItem));
      }
    } catch (err) {
      return rejectWithValue([], err);
    }
  }
);

export const addFilesToUploadQueue = createAsyncThunk(
  "upload/addFilesToUploadQueue",
  async (files, { rejectWithValue, dispatch }) => {
    try {
      for (let index = 0; index < files.length; index++) {
        await dispatch(addFileToQueue(files[index]));
      }
      dispatch(processQueue());
    } catch (err) {
      return rejectWithValue([], err);
    }
  }
);

export const updateFileStatusAndProcessNextItem = createAsyncThunk(
  "upload/updateFileStatusAndProcessNextItem",
  async (data, { dispatch }) => {
    try {
      await dispatch(
        updateFileStatus({
          id: data.id,
          status: data.status
        })
      );
      dispatch(processQueue());
    } catch (err) {
      console.log(err);
    }
  }
);

const { actions, reducer } = createSlice({
  name: "upload",
  initialState,
  reducers: {
    addFileToQueue: (state, action) => {
      const lastdotindex = action.payload.fileName.lastIndexOf(".");
      const extention = action.payload.fileName.substr(lastdotindex);
      const filename = action.payload.fileName.substr(0, lastdotindex);

      const fileName = filename + "-" + Date.now() + extention;

      const queueItem = {
        file: action.payload.file,
        fileName: fileName,
        assetId: action.payload.assetId,
        assetLink: `${assetLink}${action.payload.assetId}`,
        status: UploadStatus.PENDING,
        id: new Date().getTime(),
        size: 0,
        progress: 0,
        uploaded: 0,
        blockIds: [],
        blockQueue: []
      };
      state.uploadQueue = [...state.uploadQueue, queueItem];
      toast.success(
        `${i18next.t("upload_file")} ${action.payload.file.name} ${i18next.t(
          "upload_added_to_queue"
        )}`
      );
    },

    updateFileStatus: (state, action) => {
      let queue = state.uploadQueue.slice();
      const index = queue.findIndex(x => x.id === action.payload.id);
      queue[index].status = action.payload.status;
      switch (action.payload.status) {
        case UploadStatus.ERROR:
        case UploadStatus.CANCELLED:
        case UploadStatus.COMPLETED:
          queue[index].blockQueue = [];
          break;
        default:
          break;
      }
      state.uploadQueue = queue;
    },

    setFileBlockQueue: (state, action) => {
      let queue = state.uploadQueue.slice();
      const index = queue.findIndex(x => x.id === action.payload.id);
      queue[index].blockQueue = action.payload.blockQueue;
    },

    setFileBlockIds: (state, action) => {
      let queue = state.uploadQueue.slice();
      const index = queue.findIndex(x => x.id === action.payload.id);
      queue[index].blockIds = action.payload.blockIds;
      queue[index].size = action.payload.size;
      state.uploadQueue = queue;
    },

    setCurrentFileProgress: (state, action) => {
      let queue = state.uploadQueue.slice();
      const index = queue.findIndex(x => x.id === action.payload.id);
      const file = queue[index];

      file.uploaded = file.uploaded + action.payload.loaded;

      if (
        file.status === UploadStatus.UPLOADING &&
        file.uploaded &&
        file.progress < 100
      ) {
        file.progress = Math.floor((file.uploaded / file.size) * 100);
        state.uploadQueue = queue;
      }

      if (file.status === UploadStatus.CANCELLED) {
        file.progress = 0;
        file.uploaded = 0;
      }

      if (file.status === UploadStatus.ERROR) {
        file.progress = 0;
        file.uploaded = 0 - action.payload.loaded;
      }
    },
    setFileBlockStatus: (state, action) => {
      try {
        const queue = state.uploadQueue.slice();
        const index = queue.findIndex(x => x.id === action.payload.id);
        if (index < 0) return;
        if (!queue[index].blockQueue) return;
        let blockQueue = queue[index].blockQueue.slice();

        const blockIndex = blockQueue.findIndex(
          x => x.blockId === action.payload.blockId
        );
        if (blockIndex >= 0) {
          if (
            [UploadStatus.DONE, UploadStatus.ERROR].includes(
              action.payload.status
            )
          ) {
            blockQueue[blockIndex].block = null;
          }
          blockQueue[blockIndex].status = action.payload.status;
        }
        queue[index].blockQueue = blockQueue;
        state.uploadQueue = queue;
      } catch (error) {
        console.log(error);
      }
    }
  },
  extraReducers: builder => {}
});

export const {
  addFileToQueue,
  updateFileStatus,
  setFileBlockIds,
  setFileBlockQueue,
  setFileBlockStatus,
  setCurrentFileProgress
} = actions;

export default reducer;
